<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from "vue";
import {AxiosError} from "axios";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Icon from "@/core/components/icon/Icon.vue";
import stripeConnectService from "@/stripe-connect/services/stripeConnectService";
import {labelStripeRequirements} from "@/stripe-connect/helpers/stripeRequirementLabels";
import {OrganizationStripeAccount} from "@/stripe-connect/interfaces";
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";

/** Healthcenter holder rank, the only one allowed by the API (`can:isHealthcenterHolder`). */
const HOLDER_RANK_ID = 1;

const systemStore = useSystemStore();
const toastStore = useToastStore();

const loading = ref(true);
const fetching = ref(false);
const acting = ref(false);
const forbidden = ref(false);
const fetchFailed = ref(false);
const linkExpired = ref(false);
const account = ref<OrganizationStripeAccount | null>(null);

let fetchSequence = 0;

/**
 * The synthetic status of the connected account.
 */
const status = computed<'none' | 'incomplete' | 'pending' | 'active'>(() => {
    if (!account.value) {
        return 'none';
    }

    if (!account.value.details_submitted) {
        return 'incomplete';
    }

    return account.value.charges_enabled ? 'active' : 'pending';
});

const requirements = computed(() => labelStripeRequirements(account.value?.requirements_due ?? []));

/**
 * Fetch the account state. It reflects the server database, kept up to date
 * by the stripe `account.updated` webhook — not the onboarding navigation.
 */
const fetchAccount = async () => {
    const sequence = ++fetchSequence;
    fetching.value = true;

    try {
        const result = await stripeConnectService.getAccount();

        if (sequence !== fetchSequence) {
            return;
        }

        account.value = result;
        fetchFailed.value = false;
    } catch (error) {
        if (sequence !== fetchSequence) {
            return;
        }

        if (error instanceof AxiosError && error.response?.status === 403) {
            forbidden.value = true;
        } else {
            fetchFailed.value = true;
            toastStore.showError({content: 'Impossible de récupérer l\'état de l\'encaissement en ligne.'});
        }
    } finally {
        if (sequence === fetchSequence) {
            loading.value = false;
            fetching.value = false;
        }
    }
};

/**
 * Start the onboarding. The service creates the account when needed
 * (idempotent server side) then opens the stripe hosted window.
 *
 * On success, `acting` is only released when the onboarding window closes:
 * the modal does not block the app until the stripe page is shown, and the
 * button must not generate a second Account Link in the meantime.
 */
const startOnboarding = async () => {
    acting.value = true;
    linkExpired.value = false;

    try {
        await stripeConnectService.startOnboarding();
    } catch {
        toastStore.showError({content: 'Impossible d\'ouvrir le parcours de configuration Stripe. Veuillez réessayer.'});
        acting.value = false;
    }
};

onMounted(() => {
    if (systemStore.environment && systemStore.environment.rankId !== HOLDER_RANK_ID) {
        forbidden.value = true;
        loading.value = false;
    } else {
        fetchAccount();
    }

    stripeConnectService.onOnboardingWindowClosed((outcome) => {
        acting.value = false;
        linkExpired.value = outcome === 'refresh';
        fetchAccount();
    });
});

onUnmounted(() => {
    stripeConnectService.removeOnboardingWindowClosedListeners();
});
</script>

<template>
  <div class="flex flex-1 justify-center p-6">
    <base-flex-spinner v-if="loading" />
    <div v-else-if="forbidden" class="flex flex-1 justify-center items-center">
      <p class="text-gray-700">
        L'encaissement en ligne est réservé au titulaire de la pharmacie.
      </p>
    </div>
    <div v-else-if="fetchFailed && !account" class="flex flex-col flex-1 justify-center items-center gap-4">
      <p class="text-gray-700">
        Impossible de récupérer l'état de l'encaissement en ligne.
      </p>
      <base-button primary :loading="fetching" @click="fetchAccount">
        Réessayer
      </base-button>
    </div>
    <div v-else class="flex flex-col gap-6 w-full max-w-2xl">
      <div class="flex items-center justify-between">
        <h1 class="text-xl font-semibold text-primary">
          Encaissement en ligne
        </h1>
        <button
          v-if="account"
          class="text-sm text-gray-500 hover:text-primary flex items-center gap-1 disabled:opacity-50"
          :disabled="fetching"
          @click="fetchAccount"
        >
          <Icon name="mdi-refresh" :size="16" />
          Actualiser
        </button>
      </div>

      <div v-if="linkExpired" class="rounded-md bg-orange-50 border border-orange-200 p-3 text-sm text-orange-800">
        Le lien de configuration a expiré. Relancez la configuration pour en générer un nouveau.
      </div>

      <div class="bg-white rounded-lg shadow p-6 flex flex-col gap-4">
        <template v-if="status === 'none'">
          <p class="text-gray-700">
            Encaissez en ligne les commandes click &amp; collect de vos patients.
            Les paiements sont versés directement sur le compte bancaire de la pharmacie :
            vous êtes le commerçant, votre nom apparaît sur le relevé du patient.
          </p>
          <p class="text-sm text-gray-500">
            L'activation crée un compte Stripe pour votre officine et ouvre un parcours
            sécurisé hébergé par Stripe pour vérifier l'identité de votre établissement
            (KYC). Prévoyez un justificatif d'identité et vos coordonnées bancaires.
          </p>
          <div>
            <base-button primary :loading="acting" @click="startOnboarding">
              Activer l'encaissement en ligne
            </base-button>
          </div>
        </template>

        <template v-else>
          <div class="flex items-center gap-2">
            <div
              class="size-3 shrink-0 rounded-full"
              :class="{
                'bg-secondary': status === 'active',
                'bg-orange': status !== 'active',
              }"
            />
            <p class="font-semibold text-primary">
              <template v-if="status === 'active'">
                Encaissement en ligne actif
              </template>
              <template v-else-if="status === 'incomplete'">
                Configuration à terminer
              </template>
              <template v-else>
                Vérification en cours par Stripe
              </template>
            </p>
          </div>

          <p v-if="status === 'active'" class="text-sm text-gray-700">
            Votre pharmacie peut encaisser les commandes en ligne.
            <template v-if="!account?.payouts_enabled">
              Les versements vers votre compte bancaire sont encore en cours de validation par Stripe.
            </template>
          </p>
          <p v-else-if="status === 'incomplete'" class="text-sm text-gray-700">
            La configuration de votre compte n'a pas été finalisée.
            Reprenez le parcours Stripe pour compléter les informations de votre officine.
          </p>
          <p v-else class="text-sm text-gray-700">
            Vos informations ont bien été transmises. Stripe procède aux vérifications,
            l'état sera mis à jour automatiquement.
          </p>

          <div v-if="requirements.length" class="rounded-md bg-gray-50 border border-gray-200 p-3">
            <p class="text-sm font-medium text-gray-700 pb-1">
              Informations attendues par Stripe :
            </p>
            <ul class="text-xs text-gray-500 list-disc pl-4">
              <li v-for="requirement in requirements" :key="requirement">
                {{ requirement }}
              </li>
            </ul>
          </div>

          <div v-if="status !== 'active' || requirements.length">
            <base-button primary :loading="acting" @click="startOnboarding">
              <template v-if="status === 'incomplete'">
                Reprendre la configuration
              </template>
              <template v-else>
                Compléter mes informations
              </template>
            </base-button>
          </div>
        </template>
      </div>

      <p class="text-xs text-gray-400">
        Le parcours de vérification est hébergé par Stripe. Apodis n'a jamais accès à vos
        documents d'identité ni à vos coordonnées bancaires.
      </p>
    </div>
  </div>
</template>
